import React, { useEffect, useState } from "react";
import api, { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Inbox, Clock, CheckCircle2, Pill, Eye, Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const STATUSES = ["received", "processing", "ready", "dispensed", "cancelled"];
const STATUS_STYLE = {
  issued: "bg-blue-50 text-blue-700", sent: "bg-indigo-50 text-indigo-700", received: "bg-amber-50 text-amber-700",
  processing: "bg-amber-50 text-amber-700", ready: "bg-emerald-50 text-emerald-700", dispensed: "bg-emerald-50 text-emerald-700", cancelled: "bg-red-50 text-red-600",
};

export default function PharmacyDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = () => { setLoading(true); const params = filter !== "all" ? { status: filter } : {}; api.get("/prescriptions", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try { const { data } = await api.patch(`/prescriptions/${id}/status`, { status }); toast.success(`Marked ${status}`); setItems((p) => p.map((r) => r.id === id ? data : r)); if (active?.id === id) setActive(data); }
    catch (e) { toast.error("Could not update"); }
  };
  const counts = items.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {});

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Prescriptions Inbox</h1>
      <p className="text-slate-500 mt-1">Incoming prescriptions for {user?.clinic_name || "your pharmacy"}.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        {[["New", "issued", Inbox], ["Processing", "processing", Clock], ["Dispensed", "dispensed", CheckCircle2], ["Total", "total", Pill]].map(([label, key, Icon]) => (
          <div key={key} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between"><p className="text-sm text-slate-500">{label}</p><Icon className="w-4 h-4 text-blue-600" /></div>
            <p className="text-3xl font-bold text-slate-900 font-head mt-2">{key === "total" ? items.length : (counts[key] || 0)}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="text-xl font-semibold text-slate-900 font-head">Prescriptions</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="ph-filter" className="h-11 rounded-xl w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{["issued", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div> :
          items.length === 0 ? <div className="p-16 text-center text-slate-400"><Inbox className="w-10 h-10 mx-auto mb-3" />No prescriptions yet.</div> :
            <div className="divide-y divide-slate-100">
              {items.map((r) => (
                <div key={r.id} data-testid={`ph-rx-${r.id}`} className="flex items-center justify-between p-4 hover:bg-slate-50 gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{r.patient?.first_name} {r.patient?.last_name}</p>
                    <p className="text-sm text-slate-500 truncate">{r.medications?.map((m) => m.name).join(", ")}</p>
                    <p className="text-xs text-slate-400">Dr. {r.doctor_snapshot?.full_name} · {r.prescription_number}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                    <Button data-testid={`ph-view-${r.id}`} variant="outline" size="sm" onClick={() => setActive(r)} className="rounded-lg"><Eye className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>}
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-head">{active.patient?.first_name} {active.patient?.last_name}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">{active.prescription_number} · {active.created_at?.slice(0, 10)}</p>
              {active.allergies && <p className="text-sm text-red-600 font-medium">⚠ Allergies: {active.allergies}</p>}
              <div className="space-y-2 mt-2">
                {active.medications?.map((m, i) => (
                  <div key={i} className="text-sm border border-slate-100 rounded-lg p-2">
                    <p className="font-medium text-slate-800">{m.name} {m.strength}</p>
                    <p className="text-slate-500">{m.dosage_form} {m.route} · {m.frequency} {m.duration && `for ${m.duration}`} · Qty {m.quantity} · Refills {m.refills}</p>
                    {m.instructions && <p className="text-slate-600">Sig: {m.instructions}</p>}
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-1">Prescriber: Dr. {active.doctor_snapshot?.full_name} ({active.doctor_snapshot?.medical_reg_number})</p>
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Update status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => <Button key={s} data-testid={`ph-status-${s}`} variant={active.status === s ? "default" : "outline"} size="sm" onClick={() => updateStatus(active.id, s)} className={`rounded-lg ${active.status === s ? "bg-blue-600" : ""}`}>{s}</Button>)}
                </div>
              </div>
              <a href={`${API}/prescriptions/${active.id}/pdf?origin=${encodeURIComponent(window.location.origin)}&token=${active.public_token}`} download target="_blank" rel="noreferrer" className="block mt-4">
                <Button data-testid="ph-download-pdf" className="w-full rounded-xl h-11 bg-blue-600 hover:bg-blue-700"><Download className="w-4 h-4 mr-1.5" /> Download PDF</Button>
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
